import React, { useEffect, useRef, useState } from "react";
import { Accordion, AccordionSummary, AccordionDetails } from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import { crimeData, crimeDataNew } from "../mock/CrimeData.js";
import MenuList from "../components/MenuList";
import { useSelector } from "react-redux";

/**
 * Recursively parses stringified JSON fields in an object.
 */
const deepParseJSON = (data) => {
  if (typeof data === "string") {
    try {
      const parsed = JSON.parse(data);
      return deepParseJSON(parsed); // Recursively parse if it's still a stringified JSON
    } catch {
      return data; // Return original string if parsing fails
    }
  } else if (Array.isArray(data)) {
    return data.map(deepParseJSON);
  } else if (typeof data === "object" && data !== null) {
    return Object.fromEntries(
      Object.entries(data).map(([key, value]) => [key, deepParseJSON(value)])
    );
  }
  return data;
};

const MoveCameraWithGeoJSON = () => {
  const [map, setMap] = useState(null);
  const [mapType, setMapType] = useState("flood");
  const [postalCode, setPostalCode] = useState("");
  const mapRef = useRef(null);
  const infoWindowRef = useRef(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const selectedApplicationIdData = useSelector(
    (state) => state.intakeData.selectedApplicationIdData
  );

  let selectedApplicationIdInfo = null;
  if (selectedApplicationIdData) {
    const parsedData = JSON.parse(selectedApplicationIdData);
    selectedApplicationIdInfo = deepParseJSON(parsedData);
  }

  const cameraOptions = {
    tilt: 0,
    heading: 0,
    zoom: 7,
    center: { lat: 37.7749, lng: -122.4194 },
  };

  useEffect(() => {
    const loadGoogleMapsScript = () => {
      return new Promise((resolve, reject) => {
        if (window.google && window.google.maps) {
          resolve();
        } else {
          const script = document.createElement("script");
          script.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyAibBe9XsJ7hHfBuiihzcoSlbGp6P7IB8I&v=weekly`;
          script.defer = true;
          script.onload = resolve;
          script.onerror = () =>
            reject(new Error("Failed to load Google Maps script."));
          document.head.appendChild(script);
        }
      });
    };

    const initMap = async () => {
      if (!mapRef.current) {
        console.error("Map container not found!");
        return;
      }

      const mapInstance = new window.google.maps.Map(mapRef.current, {
        center: { lat: 37.7749, lng: -122.4194 }, // Initial center
        zoom: 12, // Initial zoom level
        mapId: "15431d2b469f209e",
      });

      setMap(mapInstance);

      // Set map loaded flag
      setMapLoaded(true);

      // Apply 3D view with initial tilt and heading after a delay
      setTimeout(() => {
        mapInstance.setTilt(45);
        mapInstance.setHeading(20);
      }, 500); // Adjust delay as needed

      // Initialize InfoWindow
      infoWindowRef.current = new window.google.maps.InfoWindow();
    };

    loadGoogleMapsScript()
      .then(initMap)
      .catch((error) =>
        console.error("Failed to load Google Maps script:", error)
      );
  }, []);

  const highlightAreas = (data) => {
    if (!map || !data.features) return;

    map.data.forEach((feature) => map.data.remove(feature));
    map.data.addGeoJson(data);

    map.data.setStyle((feature) => ({
      fillColor: "#FF0000",
      fillOpacity: 0.4,
      strokeColor: "#FF4500",
      strokeWeight: 2,
    }));

    // Add click listener for InfoWindow
    map.data.addListener("click", (event) => {
      const { feature } = event;
      const crimeDetails =
        `Crime type: ${feature.Fg["OFFENSE"]}, Address: ${feature.Fg["BLOCK"]}` ||
        "No details available";
      const position = event.latLng;

      infoWindowRef.current.setContent(
        `<div><strong>Crime Details:</strong><br>${crimeDetails}</div>`
      );
      infoWindowRef.current.setPosition(position);
      infoWindowRef.current.open(map);
    });

    // Fit map bounds to GeoJSON data
    const bounds = new window.google.maps.LatLngBounds();
    for (let i = 0; i < data.features.length; i++) {
      const eachFeature = data.features[i].geometry;
      if (eachFeature.type === "Point") {
        bounds.extend(
          new window.google.maps.LatLng(
            eachFeature.coordinates[1],
            eachFeature.coordinates[0]
          )
        );
      } else if (eachFeature.type === "MultiPoint") {
        for (let j = 0; j < eachFeature.coordinates.length; j++) {
          bounds.extend(
            new window.google.maps.LatLng(
              eachFeature.coordinates[j][1],
              eachFeature.coordinates[j][0]
            )
          );
        }
      } else if (eachFeature.type === "LineString") {
        for (let j = 0; j < eachFeature.coordinates.length; j++) {
          bounds.extend(
            new window.google.maps.LatLng(
              eachFeature.coordinates[j][1],
              eachFeature.coordinates[j][0]
            )
          );
        }
      } else if (eachFeature.type === "MultiLineString") {
        for (let j = 0; j < eachFeature.coordinates.length; j++) {
          for (let k = 0; k < eachFeature.coordinates[j].length; k++) {
            bounds.extend(
              new window.google.maps.LatLng(
                eachFeature.coordinates[j][k][1],
                eachFeature.coordinates[j][k][0]
              )
            );
          }
        }
      } else if (eachFeature.type === "Polygon") {
        for (let j = 0; j < eachFeature.coordinates.length; j++) {
          for (let k = 0; k < eachFeature.coordinates[j].length; k++) {
            bounds.extend(
              new window.google.maps.LatLng(
                eachFeature.coordinates[j][k][1],
                eachFeature.coordinates[j][k][0]
              )
            );
          }
        }
      } else if (eachFeature.type === "MultiPolygon") {
        for (let j = 0; j < eachFeature.coordinates.length; j++) {
          for (let k = 0; k < eachFeature.coordinates[j].length; k++) {
            for (let l = 0; l < eachFeature.coordinates[j][k].length; l++) {
              bounds.extend(
                new window.google.maps.LatLng(
                  eachFeature.coordinates[j][k][l][1],
                  eachFeature.coordinates[j][k][l][0]
                )
              );
            }
          }
        }
      }
    }

    map.fitBounds(bounds);
  };

  const fetchData = async (type) => {
    let url = "";
    if (type === "flood" || type === "heat") {
      url = "https://www.fema.gov/api/open/v2/FemaRegions?$format=geojson";
    } else if (type === "crime") {
      highlightAreas({ ...crimeDataNew, ...crimeData });
      return;
    }

    try {
      const response = await fetch(url);
      const data = await response.json();
      highlightAreas(data);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const handleMapTypeChange = async (event) => {
    const newMapType = event.target.value;
    setMapType(newMapType);
    await fetchData(newMapType);
  };
  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const excludedKeys = ["application_id", "company_name"];
  const sections = selectedApplicationIdInfo
    ? Object.keys(selectedApplicationIdInfo).filter(
        (key) => !excludedKeys.includes(key)
      )
    : [];

  const formatKey = (key) =>
    key.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());

  if (!sections || sections.length === 0) return null; // Check if sections exist
  // Function to determine the icon based on score
  const getIcon = (score) => {
    if (score >= 7)
      return (
        <svg
          className="w-5 h-5 text-green-600 inline-block"
          viewBox="0 0 26 26"
          fill="currentColor"
        >
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm5 13.59L13.41 12 17 8.41 15.59 7 12 10.59 8.41 7 7 8.41 10.59 12 7 15.59 8.41 17 12 13.41 15.59 17 17 15.59z" />
        </svg>
      ); // Green Checkmark

    if (score >= 4)
      return (
        <svg
          className="w-5 h-5 text-orange-500 inline-block"
          viewBox="0 0 26 26"
          fill="currentColor"
        >
          <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z" />
        </svg>
      ); // Yellow Warning

    return (
      <svg
        className="w-5 h-5 text-red-500 inline-block"
        viewBox="0 0 26 26"
        fill="currentColor"
      >
        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm5 13.59L13.41 12 17 8.41 15.59 7 12 10.59 8.41 7 7 8.41 10.59 12 7 15.59 8.41 17 12 13.41 15.59 17 17 15.59z" />
      </svg>
    ); // Red Cross
  };
  const renderValue = (value) => {
    if (typeof value === "string") {
      if (value.startsWith("http")) {
        return (
          <a
            href={value}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 underline break-all"
          >
            {value}
          </a>
        );
      }
      return <p className="break-words">{value}</p>;
    }

    if (Array.isArray(value)) {
      return (
        <ul className="list-disc ml-4 space-y-1">
          {value.map((item, index) => (
            <li key={index} className="break-words">
              {renderValue(item)}
            </li>
          ))}
        </ul>
      );
    }

    if (typeof value === "object" && value !== null) {
      return (
        <div className="border-l-4 border-gray-300 pl-4 space-y-2">
          {Object.entries(value).map(([key, nestedValue]) => (
            <div key={key} className="mt-1">
              <h4 className="font-semibold text-gray-700 capitalize">
                {formatKey(key)}
              </h4>
              <div className="text-gray-800">{renderValue(nestedValue)}</div>
            </div>
          ))}
        </div>
      );
    }

    return null;
  };

  const isNotEmpty = (value) => {
    if (!value) return false; // Handles null, undefined, false, 0, "" (empty string)
    if (Array.isArray(value)) return value.length > 0; // Check if array is not empty
    if (typeof value === "object") return Object.keys(value).length > 0; // Check if object has keys
    return true; // For non-empty strings or other valid values
  };

  return (
    <>
      <div>
        <div className="flex items-center space-x-4">
          <MenuList isOpen={isMenuOpen} toggleMenu={toggleMenu} />
          <h1 className="font-semibold py-2 text-2xl text-orange-600">
            Company Analysis
          </h1>
        </div>
      </div>
      <div className="bg-gray-100 p-4 rounded-md">
        <div className="max-w-screen-xl mx-auto grid gap-4">
          {/* Main Content */}
          <div className="flex flex-col md:flex-row gap-4">
            {/* Left Panel */}
            <div className="w-full md:w-[35%]  space-y-4">
              <div className="bg-white p-4 rounded-md h-[300px]">
                <h2 className="mb-2 text-lg font-semibold">Company Summary</h2>
                <p>
                  <span className="font-semibold inline-block pb-2 mr-3 w-[68px]">
                    Name:
                  </span>
                  {selectedApplicationIdInfo?.company_name}
                </p>
                <p>
                  <span className="font-semibold inline-block pb-2 mr-3 w-[68px]">
                    Contact:
                  </span>
                  (801)363-2270
                </p>
                <p>
                  <span className="font-semibold inline-block pb-2 mr-3 w-[68px]">
                    Email:
                  </span>
                  tricia@reliancermi.com
                </p>
                <p>
                  <span className="font-semibold inline-block pb-2 mr-3 w-[68px]">
                    Address:
                  </span>
                  150 East Stree, Murray, UT 84107
                </p>
                <div className="flex items-center">
                  <span className="font-semibold inline-block pb-2 mr-3 w-[68px]">
                    Score
                  </span>
                  <div className="w-24 h-24 bg-gray-200 rounded-full flex items-center justify-center text-xl font-bold">
                    4/6
                  </div>
                </div>
              </div>
            </div>

            {/* right Panel */}
            <div className="w-full md:w-[65%] overflow-auto min-w-0 space-y-4">
              <div className="bg-white rounded-md overflow-hidden">
                <div className="relative">
                  <div ref={mapRef} className="h-[300px] w-full"></div>
                </div>
              </div>
            </div>
          </div>

          {/* section */}
          <div className="bg-white p-4 rounded-md">
            <h2 className="mb-2 text-lg font-semibold">Score Highlights</h2>
            <ul className="flex flex-wrap justify-start">
              {sections.map((section, index) => (
                <li key={index} className="w-96 mb-2">
                  <span>
                    {getIcon(selectedApplicationIdInfo[section].score)}
                  </span>
                  <span className="pl-2">
                    {`${section}: `}
                    <span className="score-value font-semibold">{`${selectedApplicationIdInfo[section].score}`}</span>
                  </span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            {/* Main Accordion for Details Sections */}
            <div className="bg-white p-4 rounded-md">
              <h2 className="mb-0 text-lg font-semibold">Details Sections</h2>
            </div>
            <Accordion
              className="testing"
              style={{ boxShadow: "none", color: "white" }}
            >
              {/* <AccordionSummary expandIcon={<ExpandMore />}>
                <div className="bg-white p-4 rounded-md">
                  <h2 className="mb-0 text-lg font-semibold">Details Sections</h2>
                </div>
              </AccordionSummary> */}
              <AccordionDetails className="mt-0">
                <div>
                  {sections &&
                    sections.map((key, index) => {
                      const section = selectedApplicationIdInfo[key];
                      const uniqueKey = `${key}-${index}`;
                      if (!section) return null;
                      return (
                        <div key={uniqueKey} className={`py-2`}>
                          <Accordion
                            key={uniqueKey}
                            className={`bg-gray-50 border ${
                              section.score < 5
                                ? "border-red-500"
                                : section.score < 7
                                ? "border-orange-500"
                                : "border-gray-300"
                            }`}
                            style={{
                              boxShadow: "none",
                              background:
                                "rgb(243 244 246 / var(--tw-bg-opacity))",
                            }}
                            defaultExpanded
                          >
                            <AccordionSummary expandIcon={<ExpandMore />}>
                              <h2 className="font-semibold text-gray-800">
                                {key.replace(/_/g, " ")}
                              </h2>
                            </AccordionSummary>
                            <AccordionDetails>
                              <div className="space-y-3">
                                {typeof section === "object" &&
                                  Object.entries(section).map(
                                    ([subKey, value]) => {
                                      if (
                                        subKey === "score" ||
                                        subKey === "company_name"
                                      )
                                        return null;

                                      return (
                                        <>
                                          {isNotEmpty(value) && (
                                            <div key={subKey}>
                                              <Accordion
                                                key={subKey}
                                                style={{
                                                  boxShadow: "none",
                                                  background: "#fff",
                                                }}
                                              >
                                                <AccordionSummary
                                                  expandIcon={<ExpandMore />}
                                                >
                                                  <h3 className="font-semibold text-gray-700 capitalize">
                                                    {subKey.replace(/_/g, " ")}
                                                  </h3>
                                                </AccordionSummary>
                                                <AccordionDetails>
                                                  <div className="p-3 rounded-lg text-gray-800">
                                                    {renderValue(value)}
                                                  </div>
                                                </AccordionDetails>
                                              </Accordion>
                                            </div>
                                          )}
                                        </>
                                      );
                                    }
                                  )}
                              </div>
                            </AccordionDetails>
                          </Accordion>
                        </div>
                      );
                    })}
                </div>
              </AccordionDetails>
            </Accordion>
          </div>
        </div>
      </div>
    </>
  );
};

export default MoveCameraWithGeoJSON;
